import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { ViewOrderComponent } from '../view-order/view-order.component';
import { ParticularOrderService } from 'src/generated_client/api/particular-order.service';
import { ParticularOrderDto } from 'src/generated_client/model/particularOrderDto';
import { ParticularOrderStatus } from 'src/generated_client/model/particularOrderStatus';
import { DeliveryService, DeliveryOutputDto } from 'src/generated_client';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { CadetSearchDialogComponent } from '../cadet-search-dialog/cadet-search-dialog.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PageEvent } from '@angular/material/paginator';
import { MatPaginatorIntl } from '@angular/material/paginator';

@Component({
  selector: 'app-particular-orders-list',
  templateUrl: './particular-orders-list.component.html',
  styleUrls: ['./particular-orders-list.component.css'],
})
export class ParticularOrdersListComponent implements OnInit {
  readonly Array = Array;

  onViewOrder(order: ParticularOrderDto): void {
    this.dialog.open(ViewOrderComponent, {
      width: '420px',
      data: { order },
    });
  }

  onAssignCadet(order: ParticularOrderDto): void {
    // Create a reference to the dialog component
    const dialogRef = this.dialog.open(CadetSearchDialogComponent, {
      width: '500px',
      data: {
        title: 'Asignar Cadete',
        message: 'Busque y seleccione un cadete para asignar al pedido:',
        cadets: [...this.assignableCadets],
        confirmButtonText: 'Asignar',
        cancelButtonText: 'Cancelar'
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed && result.selectedValue) {
        const currentUser = this.authService.currentUser();
        if (!currentUser.cdAdmin) {
          console.error('No se pudo obtener el ID del administrador');
          return;
        }
        console.log(currentUser.cdAdmin);
        this.particularOrderService.assignCadet({
          deliveryId: Number(result.selectedValue),
          particularOrderId: order.id,
          adminId: Number(currentUser.cdAdmin),
          assignedAt: new Date()
        }).subscribe({
          next: () => {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
              width: '350px',
              data: {
                message: 'Pedido asignado correctamente',
                confirmButtonText: 'Aceptar',
                cancelButtonText: '',
                showCancelButton: false,
              },
            });
            dialogRef.afterClosed().subscribe(() => {
              this.fetchOrders();
            });
          },
          error: () => {
            this.dialog.open(ConfirmDialogComponent, {
              width: '350px',
              data: {
                message: 'Ocurrió un error al asignar el cadete',
                confirmButtonText: 'Aceptar',
                showCancelButton: false,
              },
            });
          }
        });
      }
    });
  }
  filterDateFrom: Date | null = null;
  filterDateTo: Date | null = null;
  filterStatus: number | null = null;
  filterCadete: number | null = null;
  cadetes: DeliveryOutputDto[] = [];
  assignableCadets: DeliveryOutputDto[] = [];
  filterRecipientName: string = '';
  pageNumber: number = 1;
  pageSize: number = 10;
  pageSizeOptions: number[] = [10, 25, 50, 100];
  totalCount: number = 0;

  displayedColumns: string[] = [
    'createdAt',
    'delivery',
    'client',
    'calculatedCost',
    'scheduledAt',
    'status',
    'actions',
  ];
  dataSource: ParticularOrderDto[] = [];

  statusOptions: { label: string; value: number }[] = [];

  private statusLabelMap: { [key: string]: string } = {
    Nuevo: 'Nuevo',
    Confirmado: 'Confirmado',
    Asignado: 'Asignado',
    Aceptado: 'Aceptado',
    Rechazado: 'Rechazado',
    EnCurso: 'En Curso',
    Finalizado: 'Finalizado',
    Cancelada: 'Cancelada',
  };

  private defaultHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  });

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private router: Router,
    private particularOrderService: ParticularOrderService,
    private deliveryService: DeliveryService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private paginatorIntl: MatPaginatorIntl
  ) {
    this.statusOptions = Object.entries(ParticularOrderStatus)
      .filter(([key, value]) => typeof value === 'number')
      .map(([key, value]) => ({
        label: this.statusLabelMap[key] || key,
        value: value as number,
      }));
  }

  ngOnInit(): void {
    this.paginatorIntl.itemsPerPageLabel = 'envios por página';
    this.paginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number): string => {
      if (length === 0 || pageSize === 0) {
        return `0 de ${length}`;
      }

      const startIndex = page * pageSize;
      const endIndex = Math.min(startIndex + pageSize, length);

      return `${startIndex + 1} - ${endIndex} de ${length}`;
    };
    this.paginatorIntl.changes.next();

    this.fetchOrders(true);
    this.loadCadetesForFilter();
    this.loadAssignableCadets();
  }

  loadAssignableCadets(): void {
    this.deliveryService.apiDeliveryGetAllGet().subscribe({
      next: (response) => {
        this.assignableCadets = this.extractArrayFromResponse<DeliveryOutputDto>(response, 'assignableCadets');
      },
      error: (err) => {
        this.assignableCadets = [];
        console.error('Error al cargar cadetes para asignación', err);
      }
    });
  }

  loadCadetesForFilter(): void {
    this.deliveryService.apiDeliveryGetAllGet().subscribe({
      next: (response) => {
        this.cadetes = this.extractArrayFromResponse<DeliveryOutputDto>(response, 'cadetes');
      },
      error: (err) => {
        this.cadetes = [];
        console.error('Error al cargar cadetes', err);
      }
    });
  }

  fetchOrders(resetPage: boolean = false): void {
    if (resetPage) {
      this.pageNumber = 1;
    }

    const url = `${this.particularOrderService['basePath']}/api/ParticularOrder/GetAll`;
    let params = new HttpParams()
      .set('pageNumber', String(this.pageNumber))
      .set('pageSize', String(this.pageSize));

    if (this.filterDateFrom) {
      const fromDate = new Date(this.filterDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      params = params.set('dtFrom', fromDate.toISOString());
    }

    if (this.filterDateTo) {
      const toDate = new Date(this.filterDateTo);
      toDate.setHours(23, 59, 59, 999);
      params = params.set('dtTo', toDate.toISOString());
    }

    if (this.filterStatus !== null) {
      params = params.set('status', String(this.filterStatus));
    }

    if (this.filterRecipientName.trim()) {
      params = params.set('recipientName', this.filterRecipientName.trim());
    }

    if (this.filterCadete !== null) {
      params = params.set('deliveryId', String(this.filterCadete));
    }

    this.http
      .get<ParticularOrderDto[] | { items?: ParticularOrderDto[]; data?: ParticularOrderDto[]; totalCount?: number }>(url, {
        headers: this.defaultHeaders,
        params,
        observe: 'response',
      })
      .subscribe({
        next: (response: HttpResponse<ParticularOrderDto[] | { items?: ParticularOrderDto[]; data?: ParticularOrderDto[]; totalCount?: number }>) => {
          const body = response.body;
          const orders = this.extractArrayFromResponse<ParticularOrderDto>(body, 'particularOrders');
          this.dataSource = orders;
          this.totalCount = this.getTotalCount(response, body, orders.length);
        },
        error: (err) => {
          this.dataSource = [];
          this.totalCount = 0;
          console.error('Error loading particular orders', err);
        },
      });
  }

  private extractArrayFromResponse<T>(response: unknown, source: string): T[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const candidate = response as {
      data?: unknown;
      items?: unknown;
      results?: unknown;
      result?: unknown;
    };

    const nestedCollections = [candidate.data, candidate.items, candidate.results, candidate.result];

    for (const nestedCollection of nestedCollections) {
      if (Array.isArray(nestedCollection)) {
        return nestedCollection;
      }

      if (nestedCollection && typeof nestedCollection === 'object') {
        const nestedArray = this.extractArrayFromResponse<T>(nestedCollection, source);
        if (nestedArray.length > 0) {
          return nestedArray;
        }
      }
    }

    console.warn(`Expected an array response for ${source} but received:`, response);
    return [];
  }

  private getTotalCount(
    response: HttpResponse<ParticularOrderDto[] | { items?: ParticularOrderDto[]; data?: ParticularOrderDto[]; totalCount?: number }>,
    body: ParticularOrderDto[] | { items?: ParticularOrderDto[]; data?: ParticularOrderDto[]; totalCount?: number } | null,
    fallbackCount: number
  ): number {
    const totalCountHeader = response.headers.get('X-Total-Count') || response.headers.get('x-total-count');
    if (totalCountHeader) {
      const totalFromHeader = Number(totalCountHeader);
      if (!Number.isNaN(totalFromHeader)) {
        return totalFromHeader;
      }
    }

    const paginationHeader = response.headers.get('X-Pagination') || response.headers.get('x-pagination');
    if (paginationHeader) {
      try {
        const pagination = JSON.parse(paginationHeader);
        const totalFromPagination = Number(
          pagination?.totalCount ?? pagination?.TotalCount ?? pagination?.totalItems ?? pagination?.TotalItems
        );
        if (!Number.isNaN(totalFromPagination)) {
          return totalFromPagination;
        }
      } catch (error) {
        console.warn('No se pudo parsear X-Pagination', error);
      }
    }

    if (!Array.isArray(body) && body?.totalCount !== undefined && body?.totalCount !== null) {
      return Number(body.totalCount) || fallbackCount;
    }

    return fallbackCount;
  }

  onPageChange(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.fetchOrders();
  }

  onSubmitFilters(): void {
    this.fetchOrders(true);
  }

  getStatusLabel(status: number | null | undefined): string {
    const opt = this.statusOptions.find((o) => o.value === status);
    return opt ? opt.label : '';
  }

  clearFilters(): void {
    this.filterDateFrom = null;
    this.filterDateTo = null;
    this.filterStatus = null;
    this.filterCadete = null;
    this.filterRecipientName = '';
    this.fetchOrders(true);
  }

  // Edit action
  onEditOrder(order: ParticularOrderDto): void {
    this.router.navigate(['/address-form', order.id]);
  }

  // Delete action
  onDeleteOrder(order: ParticularOrderDto): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        message: '¿Está seguro que desea eliminar este pedido?',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.particularOrderService.deleteParticularOrder(order.id).subscribe({
          next: () => {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
              width: '350px',
              data: {
                message: 'Pedido eliminado correctamente',
                confirmButtonText: 'Aceptar',
                cancelButtonText: '',
                showCancelButton: false,
              },
            });
            dialogRef.afterClosed().subscribe(() => {
              this.fetchOrders();
            });
          },
          error: (err) => {
            window.alert(
              'Error al eliminar el pedido: ' + (err?.error?.message || err)
            );
          },
        });
      }
    });
  }
}
